import { useCallback, useMemo, useRef, useState } from "react";
import {
    CheckForImporterErrorsOptions,
    useCheckForImporterErrors,
} from "@api/financialDocument.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    AccrualDocumentImporterMode,
    getAccrualDocumentImporterConfig,
} from "./accrualDocumentImporter.lib";
import { AccrualImporterContext } from "./accrualImporter.context";
import { sortAccrualImporterDocumentRows } from "./accrualImporter.sortHelpers";
import { updateDocumentRowById } from "./accrualImporterDocumentRows.helpers";
import {
    AccrualImporterDocumentRow,
    AccrualImporterSort,
} from "./accrualImporter.types";

interface Props {
    mode: AccrualDocumentImporterMode;
    readyToImportLabel: string;
    children: React.ReactNode;
}

export const AccrualImporterContextProvider: React.FC<Props> = ({
    mode,
    readyToImportLabel,
    children,
}) => {
    const config = useMemo(
        () => getAccrualDocumentImporterConfig(mode),
        [mode],
    );
    const [documentRows, setDocumentRows] = useState<
        AccrualImporterDocumentRow[]
    >([]);
    const [preview, setPreview] = useState<{
        rowId?: string;
        extractionId?: string;
    }>({});
    const previewedRowId = preview.rowId;
    const previewedExtractionId = preview.extractionId;
    const setPreviewedRowId = useCallback(
        (rowId: string | undefined, extractionId?: string) => {
            setPreview({ rowId, extractionId });
        },
        [],
    );
    const [isRefreshEnabled, setIsRefreshEnabled] = useState(true);
    const refreshSuppressionCountRef = useRef(0);
    const [sort, setSort] = useState<AccrualImporterSort | undefined>();
    const checkForImporterErrors = useCheckForImporterErrors();
    const { toast } = useToaster();

    const setRefreshSuppressed = useCallback(() => {
        refreshSuppressionCountRef.current += 1;
        setIsRefreshEnabled(refreshSuppressionCountRef.current === 0);
    }, []);

    const setRefreshUnsuppressed = useCallback(() => {
        refreshSuppressionCountRef.current = Math.max(
            0,
            refreshSuppressionCountRef.current - 1,
        );
        setIsRefreshEnabled(refreshSuppressionCountRef.current === 0);
    }, []);

    const sortedRows = useMemo(
        () => sortAccrualImporterDocumentRows({ rows: documentRows, sort }),
        [documentRows, sort],
    );

    const upsertDocumentRow = useCallback(
        (documentRow: AccrualImporterDocumentRow) => {
            const isRenderable =
                !!documentRow.financialDocument || !!documentRow.file;
            if (!isRenderable) {
                return;
            }

            setDocumentRows((prevDocumentRows) => {
                const existingDocumentRowIdx = prevDocumentRows.findIndex(
                    (row) =>
                        row.id === documentRow.id ||
                        (documentRow.financialDocument &&
                            row.financialDocument?.id ===
                                documentRow.financialDocument.id),
                );
                if (existingDocumentRowIdx !== -1) {
                    const existingRow =
                        prevDocumentRows[existingDocumentRowIdx];
                    if (
                        existingRow.financialDocument ===
                            documentRow.financialDocument &&
                        existingRow.file === documentRow.file
                    ) {
                        return prevDocumentRows;
                    }

                    return prevDocumentRows.map((row, index) =>
                        index === existingDocumentRowIdx ? documentRow : row,
                    );
                }

                return [...prevDocumentRows, documentRow];
            });
        },
        [],
    );

    const updateDocumentRow = useCallback(
        (
            id: string,
            documentRow: Partial<Omit<AccrualImporterDocumentRow, "id">>,
        ) => {
            setDocumentRows((prevDocumentRows) =>
                updateDocumentRowById({
                    rows: prevDocumentRows,
                    id,
                    update: documentRow,
                }),
            );
        },
        [],
    );

    const removeDocumentRow = useCallback(
        (documentRow: AccrualImporterDocumentRow) => {
            setDocumentRows((prevDocumentRows) =>
                prevDocumentRows.filter((row) => row.id !== documentRow.id),
            );
        },
        [],
    );

    const revalidateDocument = useCallback(
        (options: CheckForImporterErrorsOptions) => {
            checkForImporterErrors.mutate(options, {
                onSuccess: (updatedDocument) => {
                    updateDocumentRow(updatedDocument.id.toString(), {
                        financialDocument: updatedDocument,
                    });
                },
                onError: (error) => {
                    toast(
                        error instanceof Error
                            ? `Failed to refresh document: ${error.message}`
                            : "Failed to refresh document",
                    );
                },
            });
        },
        [checkForImporterErrors, toast, updateDocumentRow],
    );

    const context = useMemo(
        () => ({
            mode,
            importerQueue: config.importerQueue,
            documentRows,
            sortedRows,
            sort,
            previewedRowId,
            previewedExtractionId,
            setSort,
            setPreviewedRowId,
            upsertDocumentRow,
            removeDocumentRow,
            updateDocumentRow,
            revalidateDocument,
            readyToImportLabel,
            isRefreshEnabled,
            setRefreshSuppressed,
            setRefreshUnsuppressed,
        }),
        [
            mode,
            config.importerQueue,
            documentRows,
            sortedRows,
            sort,
            previewedRowId,
            previewedExtractionId,
            setPreviewedRowId,
            upsertDocumentRow,
            removeDocumentRow,
            updateDocumentRow,
            revalidateDocument,
            readyToImportLabel,
            isRefreshEnabled,
            setRefreshSuppressed,
            setRefreshUnsuppressed,
        ],
    );

    return (
        <AccrualImporterContext.Provider value={context}>
            {children}
        </AccrualImporterContext.Provider>
    );
};
